import { createContext, ReactNode, useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGame, usePage } from '.'
import { TriggerType } from '../../domain/enum/trigger-type.enum'
import { GameModel } from '../../domain/model/game.model'
import { MissionAnswerModel } from '../../domain/model/mission-answer.model'
import { MissionModel } from '../../domain/model/mission.model'
import { makeLoadBySlug } from '../../main/usecases/game/load-games-factory'
import { deleteMissionAnswer } from '../../main/usecases/mission-answer/delete-mission-answer-factory'
import { getAssetUrl } from '../utils/assets_helpers'
import { addLocalStorage, getLocalStorage } from '../utils/storage_helpers'
import { getTeam } from '../utils/team_helpers'
import { useMissionTrigger } from './trigger'
import {
  currentMissionHasChild,
  currentMissionIsParent,
  findParentByChild,
  getCurrentIndexInChildren,
  getCurrentIndexInGameMissions,
  getMissionTimeInSeconds,
  getTrigger,
  isLastChildMission,
  isLastParentMission,
  orderByPosition
} from '../utils/mission_helpers'
import { BgModal, ModalContent, ModalError } from './style'
import { t } from '../adapters/translate/translate'
import { GameType } from '../../domain/enum/game-type.enum'
import { MissionType } from '../../domain/enum/mission-type.enum'

type ContextProps = {
  currentMission: MissionModel|null;
  currentParent: MissionModel|null;
  currentChildren: MissionModel[]|null;
  submitButtonIsEnabled: boolean;
  timePaused: boolean;
  doSubmit: boolean;
  timeout: boolean;
  attempts: number;
  zoomModalIsOpen: boolean;
  handleToggleCurrentMission: (mission: MissionModel) => void;
  handleNextMission: () => void;
  submitCallback: (callback: () => Promise<MissionAnswerModel | any>) => void;
  activateSubmit: () => void
  handleToggleStatusSubmitButton: (status: boolean) => void;
  restoreMissionsByStorage: () => void;
  setTimeout: (timeout: boolean) => void;
  setTimePaused: (timeout: boolean) => void;
  setZoomModalIsOpen: (zoomModalIsOpen: any) => void;
  handlePreviousMission: () => void;
  handleReview: (status: boolean) => void;
  reviewCompleted: boolean;
  loadPreviousMission: boolean;
  getPreviousMission: () => MissionModel;
}

type GameWrapperProviderProps = {
  children: ReactNode;
}

const GameWrapperContext = createContext({} as ContextProps)

const GameWrapperProvider = ({ children }: GameWrapperProviderProps) => {
  const [currentMission, setCurrentMission] = useState<MissionModel|null>(null)
  const [currentParent, setCurrentParent] = useState<MissionModel|null>(null)
  const [currentChildren, setCurrentChildren] = useState<MissionModel[]>([])
  const [submitButtonIsEnabled, setSubmitButtonIsEnabled] = useState<boolean>(false)
  const [doSubmit, setDoSubmit] = useState<boolean>(false)
  const [attempts, setAttempts] = useState<number>(1)
  const [timeout, setTimeout] = useState<boolean>(false)
  const [timePaused, setTimePaused] = useState<boolean>(false)
  const [openModalError, setOpenModalError] = useState<boolean>(false)
  const [openModalText, setOpenModalText] = useState<string>('Erro ao enviar sua resposta. Tente novamente mais tarde.')
  const [zoomModalIsOpen, setZoomModalIsOpen] = useState(false)
  const [reviewCompleted, setReviewCompleted] = useState(false)
  const [loadPreviousMission, setLoadPreviousMission] = useState(false)

  const navigate = useNavigate()
  const { missionParentList, updateStatusMission, loadPoints, toggleGame, sortedMission, game } = useGame()
  const { handleToggleMissionTrigger, handleToggleTriggerVisibility, handleUpdateAction } = useMissionTrigger()
  const { toggleHeaderConfig, toggleBackground, toggleLogo, toggleBaseColor } = usePage()

  function activateSubmit () {
    setDoSubmit(true)
  }

  function handleToggleStatusSubmitButton (status: boolean) {
    setSubmitButtonIsEnabled(status)
  }

  function handleReview (status: boolean) {
    setReviewCompleted(status)
  }

  async function restoreMissionsByStorage () {
    const cMission: MissionModel|null = getLocalStorage('currentMission') && JSON.parse(String(getLocalStorage('currentMission')))
    const cParent: MissionModel|null = getLocalStorage('currentParent') && JSON.parse(String(getLocalStorage('currentParent')))
    const cChildren: MissionModel[] = getLocalStorage('currentChildren') ? JSON.parse(String(getLocalStorage('currentChildren'))) : []

    try {
      const data: GameModel = await makeLoadBySlug().load(`/${getLocalStorage('gameCode')}?team=${getTeam()?.id}`)
      toggleGame(data)

      const missionParents = data?.missions.filter(mission => mission.isParent)
      const currentMissionFiltered = data.missions.find((mission: MissionModel) => mission.uuid === cMission?.uuid)
      if (currentMissionFiltered) {
        const currentParentFiltered = missionParents.find((parent: MissionModel) => {
          if (parent.uuid === currentMissionFiltered.uuid) return true
          if (parent.childMissions.length === 0) return false
          const findChild = parent.childMissions.find(m => m.uuid === currentMissionFiltered.uuid)
          if (findChild) {
            return true
          }
          return false
        })

        toggleBackground(data.backgroundImage ? getAssetUrl('backgroundImage', data.backgroundImage) : null)
        toggleLogo(getAssetUrl('logo', data.logo))
        toggleBaseColor(data.baseColor)

        setCurrentMission(currentMissionFiltered)
        setCurrentParent(currentParentFiltered || cParent)
        setCurrentChildren(currentParentFiltered?.childMissions.sort(orderByPosition) || cChildren.sort(orderByPosition))
      }
    } catch (error) {
      console.error('GameWrapperProvider.restoreMissionsByStorage', error)
      setCurrentMission(cMission)
      setCurrentParent(cParent)
      setCurrentChildren(cChildren)
    }
  }

  async function submitCallback (cb: () => Promise<MissionAnswerModel | undefined>) {
    const data = await cb()
    if (!data) {
      if (!window.navigator.onLine) {
        setOpenModalText('Erro ao enviar sua resposta. Verifique sua conexão com a internet.')
      }
      setOpenModalError(true)
      setDoSubmit(false)
      return
    }
    let typeFeedback = 'correct_answer'

    // Verifica o tipo de feedback
    if (timeout) {
      typeFeedback = 'timeout'
      setTimePaused(true)
    } else if (data?.status === 'responded' && data?.correct) {
      setTimePaused(true)
      typeFeedback = 'correct_answer'
    } else {
      typeFeedback = 'attempt'
      if (attempts === 1) {
        setTimePaused(true)
        typeFeedback = 'wrong_answer'
      }
    }

    const hasAttempts = typeFeedback !== 'correct_answer' && attempts > 1

    // Pega o trigger atual
    let currentTrigger = currentMission?.missionTriggers.find(mt => mt.triggerType === typeFeedback)
    if (currentTrigger) {
      currentTrigger = Object.assign(
        currentTrigger,
        {
          sound: getAssetUrl('sound', currentTrigger.sound),
          buttonText: hasAttempts ? (currentTrigger.buttonText || t('Try again')) : currentTrigger.buttonText
        }
      )
    }

    const transitionTriggerAction = () => {
      const hasModalRankingAfterGroup = getTrigger(currentMission as MissionModel, 'transition' as TriggerType)
      if (hasModalRankingAfterGroup) {
        handleToggleTriggerVisibility(true)
        handleToggleMissionTrigger(hasModalRankingAfterGroup)
      }
    }

    const triggerAction = () => {
      setAttempts(state => state - 1)
      setTimeout(false)
      setTimePaused(false)
      handleToggleTriggerVisibility(false)
      if (hasAttempts && !timeout) {
        return
      }

      toggleHeaderConfig({ timeInSeconds: getMissionTimeInSeconds(currentMission?.timeLimit) })
      transitionTriggerAction()
      handleNextMission()
    }

    setDoSubmit(false)

    if (currentTrigger) {
      handleToggleTriggerVisibility(true)
      handleToggleMissionTrigger(currentTrigger)
      handleUpdateAction({ action: triggerAction })
    } else {
      setTimePaused(false)
      transitionTriggerAction()
      handleNextMission()
    }
  }

  function handleToggleCurrentMission (mission: MissionModel) {
    updateStatusMission()

    if (mission?.isParent) {
      setCurrentParent(mission)
      setCurrentChildren(mission.childMissions.sort(orderByPosition))
      addLocalStorage('currentParent', JSON.stringify(mission))
      addLocalStorage('currentChildren', JSON.stringify(mission.childMissions.sort(orderByPosition)))
    } else if (!currentParent && !currentChildren) {
      const parent = findParentByChild(mission, missionParentList)
      setCurrentParent(parent)
      setCurrentChildren(parent.childMissions.sort(orderByPosition))
      addLocalStorage('currentParent', JSON.stringify(parent))
      addLocalStorage('currentChildren', JSON.stringify(parent.childMissions.sort(orderByPosition)))
    }
    setCurrentMission({ ...mission, status: 'current' })
    setAttempts(mission?.options?.attempts || 1)
    addLocalStorage('currentMission', JSON.stringify({ ...mission, status: 'current' }))
  }

  function handleNextChild () {
    if (isLastChildMission(currentMission, currentChildren)) {
      handleEndFlow()
      return
    }
    handleToggleCurrentMission(currentChildren[getCurrentIndexInChildren(currentMission, currentChildren) + 1])
  }

  async function handleNextParent () {
    if (isLastChildMission(currentMission, currentChildren)) {
      await handleEndFlow()
      return
    }
    handleToggleCurrentMission(missionParentList[getCurrentIndexInGameMissions(currentMission, missionParentList) + 1])
  }

  async function handleNextMission () {
    let action: 'next_parent' | 'next_child' | 'first_child' = 'next_child'
    if (currentMissionIsParent(currentMission)) {
      if (currentMissionHasChild(currentMission)) {
        action = 'first_child'
      } else {
        if (!isLastParentMission(currentMission, missionParentList)) {
          action = 'next_parent'
        }
      }
    } else {
      if (currentParent) {
        action = isLastChildMission(currentMission, currentChildren) ? 'next_parent' : 'next_child'
      } else {
        action = 'next_parent'
      }
    }

    switch (action) {
      case 'next_child': return handleNextChild()
      case 'next_parent': return await handleNextParent()
      case 'first_child': return handleToggleCurrentMission(currentChildren[0])
    }
  }

  async function handleEndFlow () {
    await loadPoints()
    navigate('/')
  }

  async function handlePreviousMission () {
    setLoadPreviousMission(true)
    const previousMission = sortedMission[getCurrentIndexInGameMissions(currentMission, sortedMission) - 1]
    const team = getTeam()
    const go = true
    if (go) {
      if (game.type === GameType.TOGETHER) {
        if (previousMission.type !== MissionType.INSTRUCTIONS && previousMission.type !== MissionType.WELCOME) {
          try {
            const res = await deleteMissionAnswer().deletePreviousMissions(previousMission.uuid, currentMission?.uuid || '', team.id)
            if (res.success) {
              handleToggleCurrentMission(previousMission)
              setLoadPreviousMission(false)
              navigate('/mission')
            }
          } catch (error) {
            console.log(error)
          }
        } else {
          setOpenModalText(t('It is impossible to revert to the initial missions'))
          setOpenModalError(true)
          setLoadPreviousMission(false)
        }
      } else {
        try {
          const res = await deleteMissionAnswer().deletePreviousMissions(previousMission.uuid, currentMission?.uuid || '', team.id)
          if (res.success) {
            handleToggleCurrentMission(previousMission)
            setLoadPreviousMission(false)
            navigate('/mission')
          }
        } catch (error) {
          console.log(error)
        }
      }
    }
  }

  function getPreviousMission () {
    return sortedMission[getCurrentIndexInGameMissions(currentMission, sortedMission) - 1]
  }

  return (
    <GameWrapperContext.Provider value={{
      doSubmit,
      currentMission,
      currentParent,
      currentChildren,
      handleToggleCurrentMission,
      handleNextMission,
      submitButtonIsEnabled,
      submitCallback,
      handleToggleStatusSubmitButton,
      activateSubmit,
      restoreMissionsByStorage,
      attempts,
      timeout,
      setTimeout,
      timePaused,
      setTimePaused,
      setZoomModalIsOpen,
      zoomModalIsOpen,
      handlePreviousMission,
      handleReview,
      reviewCompleted,
      loadPreviousMission,
      getPreviousMission

    }}>
      { children }
      {openModalError &&
        <ModalError>
          <BgModal/>
          <ModalContent>
            <h2>{openModalText}</h2>
            <button
              onClick={() => {
                setTimePaused(false)
                setOpenModalError(false)
              }}
            >
              {t('Close')}
            </button>
          </ModalContent>
        </ModalError>
      }
    </GameWrapperContext.Provider>
  )
}

const useGameWrapper = () => {
  const context = useContext(GameWrapperContext)

  if (!context) throw new Error('useGameWrapper must be used within a provider GameWrapperProvider')

  return { ...context }
}

export { GameWrapperContext, useGameWrapper, GameWrapperProvider }
